import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import '../styles/quality.css'

/** How long an Undo stays on screen (and how long a deferred delete waits). */
export const UNDO_WINDOW_MS = 10_000
const INFO_MS = 6000

interface Toast {
  id: number
  message: string
  kind: 'info' | 'success' | 'error'
  /** Set on undo toasts: shows the Undo button and a countdown. */
  undo?: boolean
}

export interface UndoOptions {
  message: string
  /** Puts things back. For a change already applied, call the inverse; for a deferred one, drop it. */
  undo: () => unknown
  /** Runs when the window closes without Undo (a deferred delete sends its request here). */
  commit?: () => unknown
  /**
   * Runs instead of `commit` when the page is hidden or closed while the toast is still up. It
   * must not rely on the page staying alive: send with `fetch(..., { keepalive: true })`.
   */
  flush?: () => void
}

interface ToastContextValue {
  toast: (message: string, kind?: Toast['kind']) => void
  undoable: (opts: UndoOptions) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)
let counter = 0

/**
 * Info and success toasts clear themselves after a few seconds. Error toasts stay until
 * dismissed (WCAG 2.2.1) and are announced assertively (4.1.3); the others are polite.
 *
 * Undo toasts ("Archived “Pride Nights” · Undo") stay for UNDO_WINDOW_MS. Every timed toast
 * pauses while it is hovered or holds keyboard focus (2.2.1), and the Undo button is an ordinary
 * button in the toast region: reachable with Tab, never focused for the person.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const pending = useRef(new Map<number, UndoOptions>())
  const dismiss = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id))
  }, [])
  const toast = useCallback((message: string, kind: Toast['kind'] = 'info') => {
    const id = ++counter
    setToasts((t) => [...t, { id, message, kind }])
  }, [])
  const undoable = useCallback((opts: UndoOptions) => {
    const id = ++counter
    pending.current.set(id, opts)
    setToasts((t) => [...t, { id, message: opts.message, kind: 'info', undo: true }])
  }, [])
  const expire = useCallback(
    (id: number) => {
      const p = pending.current.get(id)
      pending.current.delete(id)
      dismiss(id)
      p?.commit?.()
    },
    [dismiss],
  )
  const runUndo = useCallback(
    (id: number) => {
      const p = pending.current.get(id)
      pending.current.delete(id)
      dismiss(id)
      p?.undo()
    },
    [dismiss],
  )
  // Leaving the page (closing the tab, following an external link, reloading) must not lose a
  // deferred delete: send whatever is still waiting with keepalive requests. Phones often discard
  // a backgrounded tab without a pagehide, so going to the background sends them too (the Undo
  // window ends early, which is safer than losing the change).
  useEffect(() => {
    const flush = () => {
      for (const [id, p] of pending.current) {
        pending.current.delete(id)
        if (p.flush) p.flush()
        else p.commit?.()
      }
      setToasts((t) => t.filter((x) => !x.undo))
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush()
    }
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('pagehide', flush)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])
  const value = useMemo(() => ({ toast, undoable }), [toast, undoable])
  const errors = toasts.filter((t) => t.kind === 'error')
  const others = toasts.filter((t) => t.kind !== 'error')
  const item = (t: Toast) => (
    <ToastItem
      key={t.id}
      t={t}
      onDismiss={() => (t.undo ? expire(t.id) : dismiss(t.id))}
      onExpire={() => (t.undo ? expire(t.id) : dismiss(t.id))}
      onUndo={() => runUndo(t.id)}
    />
  )
  return (
    <ToastContext.Provider value={value}>
      {children}
      <section className="toast-region" aria-label="Notifications">
        <div role="status" aria-live="polite">
          {others.map(item)}
        </div>
        <div role="alert">{errors.map(item)}</div>
      </section>
    </ToastContext.Provider>
  )
}

function ToastItem({
  t,
  onDismiss,
  onExpire,
  onUndo,
}: {
  t: Toast
  onDismiss: () => void
  onExpire: () => void
  onUndo: () => void
}) {
  const duration = t.undo ? UNDO_WINDOW_MS : t.kind === 'error' ? null : INFO_MS
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const paused = hovered || focused
  const remaining = useRef(duration ?? 0)
  const expireRef = useRef(onExpire)
  expireRef.current = onExpire
  useEffect(() => {
    if (duration === null || paused) return
    const started = Date.now()
    const timer = window.setTimeout(() => expireRef.current(), remaining.current)
    return () => {
      window.clearTimeout(timer)
      remaining.current = Math.max(0, remaining.current - (Date.now() - started))
    }
  }, [duration, paused])
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: hover/focus only pause the timer; the buttons inside are the controls
    <div
      className={`toast toast--${t.kind}${t.undo ? ' toast--undo' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false)
      }}
      data-paused={paused || undefined}
    >
      <span className="toast__message">{t.message}</span>
      {t.undo ? (
        <button type="button" className="toast__action" onClick={onUndo}>
          Undo
        </button>
      ) : null}
      <button
        type="button"
        className="toast__close"
        onClick={onDismiss}
        aria-label="Dismiss notification"
      >
        ✕
      </button>
      {t.undo ? (
        <span
          className="toast__timer"
          aria-hidden="true"
          style={{
            animationDuration: `${UNDO_WINDOW_MS}ms`,
            animationPlayState: paused ? 'paused' : 'running',
          }}
        />
      ) : null}
    </div>
  )
}

export function useToast() {
  const v = useContext(ToastContext)
  if (!v) throw new Error('useToast must be used inside ToastProvider')
  return v.toast
}

/** Shows an Undo toast; see UndoOptions. Higher-level helpers live in `@app/lib/undo`. */
export function useUndoToast() {
  const v = useContext(ToastContext)
  if (!v) throw new Error('useUndoToast must be used inside ToastProvider')
  return v.undoable
}
